interface ObserveScrollOptions {
  onExit?: () => void;
}

/**
 * Calls `callback` on each scroll/resize animation frame while `element`
 * is near the viewport. Uses IntersectionObserver for visibility gating
 * and requestAnimationFrame for throttling.
 */
export function observeScroll(
  element: HTMLElement,
  callback: () => void,
  signal: AbortSignal,
  options?: ObserveScrollOptions,
): void {
  if (signal.aborted) return;

  let frameId = 0;
  let isVisible = false;

  const schedule = () => {
    if (frameId || !isVisible) return;
    frameId = requestAnimationFrame(() => {
      frameId = 0;
      callback();
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const wasVisible = isVisible;
      isVisible = entries[0]?.isIntersecting ?? false;
      if (isVisible) {
        schedule();
      } else if (wasVisible) {
        options?.onExit?.();
      }
    },
    { rootMargin: "100px" },
  );

  observer.observe(element);

  window.addEventListener("scroll", schedule, { passive: true, signal });
  window.addEventListener("resize", schedule, { passive: true, signal });

  signal.addEventListener(
    "abort",
    () => {
      observer.disconnect();
      if (frameId) {
        cancelAnimationFrame(frameId);
        frameId = 0;
      }
    },
    { once: true },
  );
}
